if (!customElements.get('pakiki-purchase')) {
  customElements.define('pakiki-purchase', class extends HTMLElement {
    connectedCallback() {
      this.productInfo = this.closest('product-info');
      this.mainButton = this.productInfo?.querySelector('product-form button[name="add"]');
      if (!this.mainButton) return;
      this.form = this.mainButton.form;
      this.needsChoice = this.dataset.needsChoice === 'true';
      this.pending = false;
      this.bar = this.querySelector('.pakiki-purchase-bar');
      this.barButton = this.bar.querySelector('button');
      this.price = this.productInfo.querySelector('[id^="price-"]');
      this.sectionId = this.productInfo.dataset.originalSection || this.productInfo.dataset.section;
      this.controller = new AbortController();
      const signal = this.controller.signal;

      this.productInfo.addEventListener('click', (event) => {
        if (event.target.closest('.pakiki-guide-link')) {
          const details = this.productInfo.querySelector('[data-pakiki-size-details]');
          if (!details) return;
          details.open = true;
          const summary = details.querySelector('summary');
          summary.setAttribute('aria-expanded', 'true');
          summary.focus({ preventScroll: true });
          details.scrollIntoView({ behavior: this.scrollBehavior(), block: 'start' });
        }
        if (event.target.closest('button[name="add"]') === this.mainButton && this.needsChoice) {
          event.preventDefault();
          this.focusSize();
        }
      }, { signal });

      this.productInfo.addEventListener('submit', (event) => {
        if (event.target !== this.form) return;
        if (this.needsChoice || this.pending || this.mainButton.disabled) {
          event.preventDefault();
          event.stopImmediatePropagation();
          if (this.needsChoice) this.focusSize();
        }
      }, { capture: true, signal });

      this.productInfo.addEventListener('change', (event) => {
        if (!event.target.closest('variant-selects')) return;
        if (this.sizeGroup()?.contains(event.target)) this.needsChoice = false;
        this.pending = true;
        this.sync();
      }, { capture: true, signal });

      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data.sectionId !== this.sectionId || !this.pending) return;
        this.pending = false;
        if (this.needsChoice) this.clearSize();
        else {
          this.querySelector('.pakiki-choice-hint').textContent = '';
          const group = this.sizeGroup();
          const size = data.variant.options[Number(this.dataset.sizeIndex)];
          group?.querySelectorAll('input[type="radio"]').forEach(input => { input.checked = input.value === size; });
          const select = group?.querySelector('select');
          if (select) select.value = size;
          this.mainButton.type = 'submit';
          this.productInfo.querySelector('pakiki-size-guide')?.highlight?.(size);
        }
        this.sync();
      });

      this.unsubscribeError = subscribe(PUB_SUB_EVENTS.cartError, ({ productVariantId }) => {
        if (String(productVariantId) !== this.form.querySelector('[name="id"]')?.value) return;
        this.mainButton.scrollIntoView({ behavior: this.scrollBehavior(), block: 'center' });
      });

      this.barButton.addEventListener('click', () => {
        if (this.needsChoice) this.focusSize();
        else if (!this.pending && !this.mainButton.disabled && this.mainButton.getAttribute('aria-disabled') !== 'true') this.mainButton.click();
      }, { signal });

      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(this.mainButton, { attributes: true, childList: true, subtree: true, characterData: true });
      if (this.price) this.observer.observe(this.price, { childList: true, subtree: true, characterData: true });
      if (this.needsChoice) this.clearSize();
      this.sync();

      if (this.dataset.sticky === 'true' && !this.closest('quick-add-modal')) {
        this.media = matchMedia('(max-width: 749px)');
        this.media.addEventListener('change', () => this.updateVisibility(), { signal });
        const scheduleVisibility = () => {
          if (this.visibilityFrame) return;
          this.visibilityFrame = requestAnimationFrame(() => {
            this.visibilityFrame = null;
            this.updateVisibility();
          });
        };
        this.intersection = new IntersectionObserver(scheduleVisibility);
        this.intersection.observe(this.mainButton);
        // A fast scroll can skip the button without changing its intersection state.
        window.addEventListener('scroll', scheduleVisibility, { passive: true, signal });
        window.addEventListener('resize', scheduleVisibility, { passive: true, signal });
        document.addEventListener('focusin', scheduleVisibility, { signal });
        document.addEventListener('focusout', scheduleVisibility, { signal });
        this.updateVisibility();
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.unsubscribe?.();
      this.unsubscribeError?.();
      this.observer?.disconnect();
      this.intersection?.disconnect();
      cancelAnimationFrame(this.visibilityFrame);
      if (this.media) document.body.classList.remove('pakiki-purchase-visible');
    }

    sizeGroup() { return this.productInfo.querySelectorAll('variant-selects .product-form__input')[Number(this.dataset.sizeIndex)]; }
    scrollBehavior() { return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'; }

    clearSize() {
      this.sizeGroup()?.querySelectorAll('input[type="radio"]').forEach(input => { input.checked = false; });
      const select = this.sizeGroup()?.querySelector('select');
      if (select) select.value = '';
      this.form.querySelector('[name="id"]').value = '';
      this.mainButton.type = 'submit';
      this.mainButton.disabled = false;
      this.mainButton.querySelector('span').textContent = this.dataset.chooseLabel;
      this.productInfo.querySelector('pakiki-size-guide')?.highlight?.();
    }

    focusSize() {
      const group = this.sizeGroup();
      const input = group?.querySelector('input:not(.disabled), select');
      input?.focus({ preventScroll: true });
      group?.scrollIntoView({ behavior: this.scrollBehavior(), block: 'center' });
      const hint = this.querySelector('.pakiki-choice-hint');
      hint.textContent = hint.dataset.message;
    }

    sync() {
      const sale = this.price?.querySelector('.price--on-sale');
      const price = sale?.querySelector('.price-item--sale') || this.price?.querySelector('.price__regular .price-item--regular');
      this.bar.querySelector('.pakiki-purchase-bar__price').textContent = price?.textContent.trim() || '';
      const size = this.sizeGroup()?.querySelector('input:checked, select')?.value;
      this.bar.querySelector('.pakiki-purchase-bar__size').textContent = this.needsChoice || !size ? this.dataset.chooseLabel : this.dataset.sizeLabel.replace('__SIZE__', size);
      this.barButton.textContent = this.needsChoice ? this.dataset.chooseLabel : this.mainButton.querySelector('span')?.textContent.trim();
      this.barButton.disabled = this.pending || this.mainButton.disabled || this.mainButton.getAttribute('aria-disabled') === 'true';
    }

    updateVisibility() {
      const focused = document.activeElement;
      const typing = focused?.matches('textarea, input:not([type="radio"]):not([type="checkbox"]):not([type="button"]), [contenteditable="true"]');
      const visible = this.media?.matches && this.mainButton.getBoundingClientRect().bottom < 0 && !typing;
      this.bar.hidden = !visible;
      document.body.classList.toggle('pakiki-purchase-visible', !!visible);
    }
  });
}
